import React, { useState } from 'react';
import { base44 } from '@/api/base44Client';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Send, StickyNote } from 'lucide-react';
import { format } from 'date-fns';

export default function OrderInternalNotes({ orderId }) {
  const queryClient = useQueryClient();
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  const { data: notes = [], isLoading } = useQuery({
    queryKey: ['order-notes', orderId],
    queryFn: () => base44.entities.OrderInternalNote.filter({ order_id: orderId }, '-created_date'),
  });

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!note.trim()) return;
    setSaving(true);
    await base44.functions.invoke('adminOrderNote', { orderId, note });
    await queryClient.invalidateQueries({ queryKey: ['order-notes', orderId] });
    setNote('');
    setSaving(false);
  };

  return (
    <div className="space-y-3">
      <form onSubmit={handleAdd} className="flex gap-2">
        <textarea
          value={note}
          onChange={e => setNote(e.target.value)}
          placeholder="Add an internal note (not visible to customer)…"
          rows={2}
          className="flex-1 bg-[#111115] border border-white/10 text-white text-xs px-3 py-2 resize-none focus:outline-none focus:border-yellow-500/40 placeholder:text-white/20"
        />
        <button
          type="submit"
          disabled={saving || !note.trim()}
          className="bg-yellow-500/20 hover:bg-yellow-500/30 text-yellow-400 border border-yellow-500/20 px-3 py-2 text-xs font-bold transition-colors disabled:opacity-40 disabled:cursor-not-allowed self-stretch flex items-center"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        </button>
      </form>

      {isLoading ? (
        <div className="flex justify-center py-4"><Loader2 className="w-4 h-4 animate-spin text-primary" /></div>
      ) : notes.length === 0 ? (
        <p className="text-white/20 text-xs text-center py-4">No notes yet</p>
      ) : (
        <div className="space-y-2 max-h-48 overflow-y-auto">
          {notes.map(n => (
            <div key={n.id} className="bg-yellow-500/5 border border-yellow-500/10 px-3 py-2.5">
              <div className="flex items-center justify-between mb-1">
                <span className="text-yellow-400 text-[10px] font-bold">{n.admin_name || n.admin_email}</span>
                <span className="text-white/20 text-[10px]">{n.created_date ? format(new Date(n.created_date), 'dd MMM yyyy HH:mm') : '—'}</span>
              </div>
              <p className="text-white/60 text-xs leading-relaxed">{n.note}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}